import { useCallback, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useChatStore } from '@/stores/chatStore';
import { useToast } from '@/contexts/ToastContext';
import { useAuthContext } from '@/contexts/AuthContext';
import type { WorkPlan, Message } from '@/types';
import { conversationsApi } from '@/api/conversations';

const API_BASE = import.meta.env.VITE_API_URL || '';

export type AiProvider = 'Claude' | 'Deepseek';

interface StartChatOptions {
  title?: string;
  autoApprove?: boolean;
  attachments?: string[];
  provider?: AiProvider;
}

interface SendMessageOptions {
  autoApprove?: boolean;
  attachments?: string[];
  provider?: AiProvider;
}

export function useChatSignalR(projectId: string) {
  const {
    messages,
    currentStreamingMessage,
    workPlan,
    isStreaming,
    error,
    thinkingStatus,
    terminalOutput,
    fileOperations,
    buildStatus,
    deployStatus,
    lastFileChange,
    thinkingBlocks,
    toolOperations,
    pendingQuestion,
    userPreferences,
    addMessage,
    appendToStreamingMessage,
    finalizeStreamingMessage,
    setWorkPlan,
    updateWorkPlan,
    updateWorkPlanPhase,
    updateWorkPlanTask,
    setStreaming,
    setError,
    setThinkingStatus,
    addTerminalOutput,
    addFileOperation,
    setBuildStatus,
    setDeployStatus,
    updateThinkingBlock,
    addToolOperation,
    updateToolOperation,
    setPendingQuestion,
    reset,
  } = useChatStore();

  const toast = useToast();
  const { accessToken } = useAuthContext();
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const activeTasksRef = useRef<Map<string, string>>(new Map()); // taskId -> taskType

  // Create SignalR connection
  const createConnection = useCallback(async () => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      return connectionRef.current;
    }

    if (!accessToken) {
      throw new Error('No authentication token');
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_BASE}/hubs/chat`, {
        accessTokenFactory: () => accessToken,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Set up event handlers
    connection.on('Connected', (data) => {
      console.log('[SignalR] Connected:', data);
      setIsConnected(true);
    });

    connection.on('Thinking', (data: { status: string }) => {
      console.log('[SignalR] Thinking:', data);
      setThinkingStatus(data.status || 'Processing...');
    });

    connection.on('TextDelta', (data: { delta: string }) => {
      if (data.delta) {
        setThinkingStatus(null);
        appendToStreamingMessage(data.delta);
      }
    });

    connection.on('MessageComplete', (data: { messageId: string; tokensUsed: number; creditsUsed: number }) => {
      console.log('[SignalR] MessageComplete:', data);
      finalizeStreamingMessage();
      setThinkingStatus(null);
    });

    connection.on('WorkPlanCreated', (data: { workPlanId: string; title: string; totalPhases: number; phases: unknown[] }) => {
      console.log('[SignalR] WorkPlanCreated:', data);
      setWorkPlan({
        id: data.workPlanId,
        title: data.title,
        totalPhases: data.totalPhases,
        phases: data.phases,
        status: 'InProgress',
        completedPhases: 0,
      } as WorkPlan);
      setThinkingStatus('Executing work plan...');
    });

    connection.on('PhaseStarted', (data: { phaseId: string; phaseName: string; phaseIndex: number }) => {
      console.log('[SignalR] PhaseStarted:', data);
      updateWorkPlanPhase(data.phaseId, {
        status: 'InProgress',
        startedAt: new Date().toISOString(),
      });
      setThinkingStatus(`Starting: ${data.phaseName}`);
    });

    connection.on('PhaseCompleted', (data: { phaseId: string; phaseName: string; success: boolean; errorMessage?: string }) => {
      console.log('[SignalR] PhaseCompleted:', data);
      updateWorkPlanPhase(data.phaseId, {
        status: data.success ? 'Completed' : 'Failed',
        completedAt: new Date().toISOString(),
        errorMessage: data.errorMessage,
      });
      if (data.success) {
        updateWorkPlan({
          completedPhases: (workPlan?.completedPhases || 0) + 1,
        });
      }
    });

    connection.on('TaskStarted', (data: { taskId: string; taskType: string; description: string }) => {
      console.log('[SignalR] TaskStarted:', data);
      setThinkingStatus(data.description || 'Processing task...');

      // Track active task for completion handling
      activeTasksRef.current.set(data.taskId, data.taskType);

      if (data.taskType === 'BuildProject') {
        setBuildStatus('building');
      } else if (data.taskType === 'DeployProject') {
        setDeployStatus('deploying');
      }
    });

    connection.on('TaskCompleted', (data: { taskId: string; success: boolean; outputSummary?: string; errorMessage?: string }) => {
      console.log('[SignalR] TaskCompleted:', data);

      // Get task type from tracking and clean up
      const taskType = activeTasksRef.current.get(data.taskId);
      activeTasksRef.current.delete(data.taskId);

      // Update status based on task type
      if (taskType === 'BuildProject') {
        setBuildStatus(data.success ? 'success' : 'failed');
        if (!data.success && data.errorMessage) {
          // Don't show toast here - RetryingBuild event will handle it if retry is happening
          // toast.error('Build Failed', data.errorMessage.substring(0, 150));
          // Instead, set a status that indicates retry might be coming
          setThinkingStatus('Build failed, checking for retries...');
        } else {
          setThinkingStatus(null);
        }
      } else if (taskType === 'DeployProject') {
        setDeployStatus(data.success ? 'success' : 'failed');
        setBuildStatus(data.success ? 'success' : 'failed'); // Build is part of deploy
        if (!data.success && data.errorMessage) {
          toast.error('Deployment Failed', data.errorMessage.substring(0, 150));
        }
        setThinkingStatus(null);
      } else {
        // Clear thinking status for other completed tasks
        setThinkingStatus(null);
      }
    });

    connection.on('FileOperation', (data: { operation: string; filePath: string; success: boolean }) => {
      console.log('[SignalR] FileOperation:', data);
      addFileOperation({
        operation: data.operation as 'create' | 'edit' | 'delete',
        path: data.filePath,
        timestamp: new Date(),
      });
    });

    connection.on('CommandOutput', (data: { command: string; output: string; exitCode?: number }) => {
      console.log('[SignalR] CommandOutput:', data);
      addTerminalOutput({
        command: data.command,
        output: data.output,
        exitCode: data.exitCode,
        timestamp: new Date(),
      });
    });

    connection.on('ApprovalRequired', (data: { phaseId: string; phaseName: string; description: string }) => {
      console.log('[SignalR] ApprovalRequired:', data);
      updateWorkPlan({ status: 'AwaitingApproval' });
      setThinkingStatus(null);
      toast.info('Approval Required', `Phase "${data.phaseName}" requires your approval.`);
    });

    connection.on('Error', (data: { message: string; code?: string; recoverable: boolean }) => {
      console.log('[SignalR] Error:', data);
      setError(data.message);
      setThinkingStatus(null);
      if (!data.recoverable) {
        toast.error('Error', data.message);
      }
    });

    connection.on('Done', (data: { workPlanId?: string; totalTokensUsed: number; totalCreditsUsed: number }) => {
      console.log('[SignalR] Done:', data);
      setThinkingStatus(null);
      setStreaming(false);
      if (data.workPlanId) {
        updateWorkPlan({ status: 'Completed', completedAt: new Date().toISOString() });
      }
    });

    connection.on('Joined', (projectId: string) => {
      console.log('[SignalR] Joined project:', projectId);
    });

    // Agentic AI event handlers
    connection.on('ThinkingBlock', (data: { blockId: string; content: string; isComplete: boolean }) => {
      console.log('[SignalR] ThinkingBlock:', data);
      updateThinkingBlock(data.blockId, data.content, data.isComplete);
    });

    connection.on('ToolUseStart', (data: { toolId: string; toolName: string; description: string; input?: unknown }) => {
      console.log('[SignalR] ToolUseStart:', data);
      addToolOperation({
        id: data.toolId,
        toolName: data.toolName,
        description: data.description,
        status: 'running',
      });
      setThinkingStatus(data.description || `Using ${data.toolName}...`);
    });

    connection.on('ToolUseEnd', (data: { toolId: string; success: boolean; summary?: string; errorMessage?: string }) => {
      console.log('[SignalR] ToolUseEnd:', data);
      updateToolOperation(data.toolId, {
        status: data.success ? 'success' : 'error',
        summary: data.summary,
        errorMessage: data.errorMessage,
      });
    });

    connection.on('FileRead', (data: { filePath: string; lineCount: number; language?: string; preview?: string }) => {
      console.log('[SignalR] FileRead:', data);
      addFileOperation({
        operation: 'create', // Using 'create' for reads since we don't have a 'read' type
        path: data.filePath,
        timestamp: new Date(),
      });
    });

    connection.on('UserQuestion', (data: { questionId: string; question: string; options?: { id: string; label: string; description?: string }[]; allowMultiple: boolean; allowCustom: boolean; context?: string }) => {
      console.log('[SignalR] UserQuestion:', data);
      setPendingQuestion({
        questionId: data.questionId,
        question: data.question,
        options: data.options,
        allowMultiple: data.allowMultiple,
        allowCustom: data.allowCustom,
        context: data.context,
      });
      setThinkingStatus(null);
      toast.info('Question from AI', 'The AI needs your input to continue.');
    });

    connection.on('RetryingBuild', (data: { attemptNumber: number; maxAttempts: number; errorMessage: string; status: string }) => {
      console.log('[SignalR] RetryingBuild:', data);
      setThinkingStatus(data.status);
      setBuildStatus('building');
      setStreaming(true); // Re-enable streaming state for the retry
      toast.warning('Build Failed', `Retrying (${data.attemptNumber}/${data.maxAttempts}): ${data.errorMessage.substring(0, 100)}...`);
    });

    connection.on('DeploymentSuccess', (data: { previewUrl: string; projectSlug: string; isFirstDeployment: boolean }) => {
      console.log('[SignalR] DeploymentSuccess:', data);
      setDeployStatus('success');
      setBuildStatus('success');
      toast.success('Deployment Successful', data.isFirstDeployment ? 'Your app is now live!' : 'Your app has been updated.');

      // Auto-open preview URL in new tab
      if (data.previewUrl) {
        window.open(data.previewUrl, '_blank');
      }
    });

    connection.onreconnecting((error) => {
      console.log('[SignalR] Reconnecting...', error);
      setConnectionError('Reconnecting...');
    });

    connection.onreconnected((connectionId) => {
      console.log('[SignalR] Reconnected:', connectionId);
      setConnectionError(null);
      setIsConnected(true);
    });

    connection.onclose((error) => {
      console.log('[SignalR] Connection closed:', error);
      setIsConnected(false);
      setStreaming(false);
    });

    connectionRef.current = connection;
    return connection;
  }, [
    accessToken,
    appendToStreamingMessage,
    finalizeStreamingMessage,
    setWorkPlan,
    updateWorkPlan,
    updateWorkPlanPhase,
    setError,
    setThinkingStatus,
    addFileOperation,
    addTerminalOutput,
    setBuildStatus,
    setDeployStatus,
    setStreaming,
    toast,
    workPlan?.completedPhases,
    // Agentic AI actions
    updateThinkingBlock,
    addToolOperation,
    updateToolOperation,
    setPendingQuestion,
  ]);

  // Connect and join project
  const connect = useCallback(async (): Promise<signalR.HubConnection> => {
    try {
      const connection = await createConnection();
      if (connection.state !== signalR.HubConnectionState.Connected) {
        await connection.start();
        console.log('[SignalR] Connected to hub');
        setIsConnected(true);
        setConnectionError(null);
      }
      // Join project room
      await connection.invoke('JoinProject', projectId);
      return connection;
    } catch (err) {
      console.error('[SignalR] Connection error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Connection failed';
      setConnectionError(errorMessage);
      setIsConnected(false);
      throw new Error(errorMessage);
    }
  }, [createConnection, projectId]);

  // Disconnect
  const disconnect = useCallback(async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.invoke('LeaveProject', projectId);
        await connectionRef.current.stop();
      } catch (err) {
        console.error('[SignalR] Disconnect error:', err);
      }
      setIsConnected(false);
    }
  }, [projectId]);

  // Auto-connect on mount
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Start a new chat
  const startChat = useCallback(async (
    content: string,
    options: StartChatOptions = {}
  ) => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      await connect();
    }

    setStreaming(true);
    setError(null);

    // Add user message immediately
    addMessage({
      role: 'User',
      content,
      createdAt: new Date(),
    });

    try {
      await connectionRef.current!.invoke('StartChat', {
        projectId,
        message: content,
        title: options.title,
        autoApprove: options.autoApprove ?? true,
        attachments: options.attachments,
        provider: options.provider === 'Claude' ? 0 : options.provider === 'Deepseek' ? 2 : 2, // Map to enum: Claude=0, Deepseek=2
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start chat';
      setError(message);
      toast.error('Chat Error', message);
      setStreaming(false);
    }
  }, [projectId, addMessage, setStreaming, setError, toast, connect]);

  // Send a message to existing conversation
  const sendMessage = useCallback(async (
    content: string,
    conversationId: string,
    options: SendMessageOptions = {}
  ) => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      await connect();
    }

    setStreaming(true);
    setError(null);

    // Add user message immediately
    addMessage({
      role: 'User',
      content,
      createdAt: new Date(),
    });

    try {
      await connectionRef.current!.invoke('SendMessage', {
        conversationId,
        message: content,
        autoApprove: options.autoApprove ?? true,
        attachments: options.attachments,
        provider: options.provider ? (options.provider === 'Claude' ? 0 : 2) : undefined, // Map to enum if provided
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send message';
      setError(message);
      toast.error('Chat Error', message);
      setStreaming(false);
    }
  }, [addMessage, setStreaming, setError, toast, connect]);

  // Load existing conversation
  const loadConversation = useCallback(async (
    conversationId: string
  ): Promise<{ hasPendingResponse: boolean; messages: Message[] }> => {
    try {
      const conversation = await conversationsApi.get(conversationId);
      conversationIdRef.current = conversationId;

      const userMessages = conversation.messages.filter(m => m.role === 'User');
      const assistantMessages = conversation.messages.filter(m => m.role === 'Assistant');
      const lastUserMessage = userMessages[userMessages.length - 1];
      const lastUserTime = lastUserMessage?.createdAt ? new Date(lastUserMessage.createdAt).getTime() : 0;
      const hasAiResponseAfterLastUser = lastUserMessage && assistantMessages.some(
        m => m.createdAt && new Date(m.createdAt).getTime() > lastUserTime
      );

      const hasPendingResponse = userMessages.length > 0 && !hasAiResponseAfterLastUser;

      if (conversation.messages.length > 0) {
        conversation.messages.forEach(msg => {
          addMessage({
            role: msg.role,
            content: msg.content,
            createdAt: msg.createdAt ? new Date(msg.createdAt) : new Date(),
          });
        });
      }

      return { hasPendingResponse, messages: conversation.messages };
    } catch (err) {
      console.error('Failed to load conversation:', err);
      return { hasPendingResponse: false, messages: [] };
    }
  }, [addMessage]);

  // Continue existing conversation
  const continueConversation = useCallback(async (
    conversationId: string,
    initialMessage?: string,
    options: { autoApprove?: boolean } = {}
  ) => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      await connect();
    }

    setStreaming(true);
    setError(null);
    conversationIdRef.current = conversationId;

    if (initialMessage) {
      addMessage({
        role: 'User',
        content: initialMessage,
        createdAt: new Date(),
      });
    }

    setThinkingStatus('AI is analyzing your request...');

    try {
      await connectionRef.current!.invoke('ContinueConversation', conversationId, options.autoApprove ?? true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to continue conversation';
      setError(message);
      toast.error('Chat Error', message);
      setStreaming(false);
    }
  }, [addMessage, setStreaming, setError, setThinkingStatus, toast, connect]);

  // Submit response to a pending question from the AI
  const submitQuestionResponse = useCallback(async (
    questionId: string,
    response: string | string[],
    conversationId: string
  ) => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      await connect();
    }

    setStreaming(true);
    setError(null);
    setPendingQuestion(null);

    // Add user's answer as a message
    const answerText = Array.isArray(response) ? response.join(', ') : response;
    addMessage({
      role: 'User',
      content: answerText,
      createdAt: new Date(),
    });

    try {
      await connectionRef.current!.invoke('SubmitQuestionResponse', {
        questionId,
        conversationId,
        response: Array.isArray(response) ? response : [response],
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit response';
      setError(message);
      toast.error('Chat Error', message);
      setStreaming(false);
    }
  }, [addMessage, setStreaming, setError, setPendingQuestion, toast, connect]);

  // Cancel current operation
  const cancel = useCallback(() => {
    // Can't really cancel SignalR invocations, but we can reset state
    setStreaming(false);
    setThinkingStatus(null);
  }, [setStreaming, setThinkingStatus]);

  return {
    messages,
    currentStreamingMessage,
    workPlan,
    isStreaming,
    error,
    conversationId: conversationIdRef.current,
    thinkingStatus,
    terminalOutput,
    fileOperations,
    buildStatus,
    deployStatus,
    lastFileChange,
    isConnected,
    connectionError,
    // Agentic AI state
    thinkingBlocks,
    toolOperations,
    pendingQuestion,
    userPreferences,
    // Actions
    startChat,
    sendMessage,
    loadConversation,
    continueConversation,
    submitQuestionResponse,
    cancel,
    reset,
    connect,
    disconnect,
  };
}
